// Scroll position management utility
class ScrollPositionManager {
  constructor() {
    this.storageKey = 'vue-router-scroll-positions';
    this.positions = this.loadPositions();
  }

  // Load saved scroll positions from localStorage
  loadPositions() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.warn('Error loading scroll positions:', error);
      return {};
    }
  }

  // Save scroll positions to localStorage
  savePositions() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.positions));
    } catch (error) {
      console.warn('Error saving scroll positions:', error);
    }
  }

  // Save current scroll position for a route
  savePosition(route, scrollX, scrollY) {
    const key = this.getRouteKey(route);
    this.positions[key] = { x: scrollX, y: scrollY, timestamp: Date.now() };
    // Save positions with a delay to avoid excessive localStorage writes
    this.debouncedSave();
  }

  // Get saved scroll position for a route
  getPosition(route) {
    const key = this.getRouteKey(route);
    const position = this.positions[key];
    
    // Only return position if it's less than 1 hour old
    if (position && (Date.now() - position.timestamp) < 3600000) {
      return { x: position.x, y: position.y };
    }
    
    return null;
  }

  // Clear old positions (older than 1 hour)
  cleanup() {
    const now = Date.now();
    const oneHour = 3600000;
    
    Object.keys(this.positions).forEach(key => {
      if (this.positions[key] && (now - this.positions[key].timestamp) > oneHour) {
        delete this.positions[key];
      }
    });
    
    this.savePositions();
  }

  // Get a unique key for a route
  getRouteKey(route) {
    // Include path and query params to differentiate between different page states
    return `${route.path}${JSON.stringify(route.query || {})}`;
  }

  // Debounced save to prevent excessive writes
  debouncedSave() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    
    this.saveTimeout = setTimeout(() => {
      this.savePositions();
    }, 100);
  }

  // Initialize scroll position tracking
  init(router) {
    // Save position before route change
    router.beforeEach((to, from) => {
      if (typeof window !== 'undefined') {
        this.savePosition(from, window.scrollX, window.scrollY);
      }
    });

    // Clean up old positions periodically
    setInterval(() => {
      this.cleanup();
    }, 300000); // Every 5 minutes

    // Clean up on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.savePosition(router.currentRoute.value, window.scrollX, window.scrollY);
      });
    }
  }
}

// Create and export singleton instance
export const scrollPositionManager = new ScrollPositionManager();

// Export the class for testing or custom instances
export { ScrollPositionManager };
